import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  ToggleSwitch,
  TOGGLE_SWITCH_PREVIEW_STATE_LABELS,
  TOGGLE_SWITCH_PREVIEW_STATES,
  TOGGLE_SWITCH_VALUE_STATE_LABELS,
  TOGGLE_SWITCH_VALUE_STATES,
  type ToggleSwitchValueState,
} from '@components/ui'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './ToggleSwitchShowcase.css'

const getValueStateProps = (
  valueState: ToggleSwitchValueState,
): { checked: boolean } => ({
  checked: valueState === 'on',
})

const ToggleSwitchShowcase: React.FC = () => {
  const [checked, setChecked] = useState(false)
  const [disabled, setDisabled] = useState(false)
  const [darkMode, setDarkMode] = useState(false)
  const [highlightedValueState, setHighlightedValueState] =
    useState<ToggleSwitchValueState>('off')
  const rowRefs = useRef<
    Partial<Record<ToggleSwitchValueState, HTMLDivElement | null>>
  >({})

  useEffect(() => {
    rowRefs.current[highlightedValueState]?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    })
  }, [highlightedValueState])

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'toggle-highlight-value',
            label: 'Highlighted value',
            control: (
              <select
                id="toggle-highlight-value"
                className="ui-field-control"
                value={highlightedValueState}
                onChange={(event) =>
                  setHighlightedValueState(
                    event.target.value as ToggleSwitchValueState,
                  )
                }
              >
                {TOGGLE_SWITCH_VALUE_STATES.map((option) => (
                  <option key={option} value={option}>
                    {TOGGLE_SWITCH_VALUE_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
        ],
      },
      {
        title: 'State',
        fields: [
          {
            id: 'toggle-checked',
            label: 'On',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="toggle-checked"
                  type="checkbox"
                  checked={checked}
                  onChange={(event) => setChecked(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'toggle-disabled',
            label: 'Disabled',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="toggle-disabled"
                  type="checkbox"
                  checked={disabled}
                  onChange={(event) => setDisabled(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'toggle-dark-mode',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="toggle-dark-mode"
                  type="checkbox"
                  checked={darkMode}
                  onChange={(event) => setDarkMode(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [checked, darkMode, disabled, highlightedValueState],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Toggle switch"
        description="Binary on/off control with default, hover, focus, and disabled states."
        stageClassName="ui-preview__stage--wide toggle-switch-showcase__stage-wrap"
      >
        <div
          className={`toggle-switch-showcase__sheet${
            darkMode ? ' toggle-switch-showcase__sheet--dark' : ''
          }`}
        >
          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Interactive toggle switch with the selected on/off and disabled
              state.
            </p>
            <ToggleSwitch
              checked={checked}
              disabled={disabled}
              darkMode={darkMode}
              aria-label="Toggle switch preview"
              onChange={setChecked}
            />
          </section>

          <div className="toggle-switch-showcase__matrix-header">
            <span className="toggle-switch-showcase__matrix-corner">Value</span>
            {TOGGLE_SWITCH_PREVIEW_STATES.map((state) => (
              <span
                key={state}
                className="toggle-switch-showcase__matrix-header-cell"
              >
                {TOGGLE_SWITCH_PREVIEW_STATE_LABELS[state]}
              </span>
            ))}
          </div>

          {TOGGLE_SWITCH_VALUE_STATES.map((valueState) => (
            <div
              key={valueState}
              ref={(element) => {
                rowRefs.current[valueState] = element
              }}
              className={`toggle-switch-showcase__matrix-row${
                valueState === highlightedValueState
                  ? ' toggle-switch-showcase__matrix-row--selected'
                  : ''
              }${darkMode ? ' toggle-switch-showcase__matrix-row--dark' : ''}`}
            >
              <span className="toggle-switch-showcase__matrix-row-label">
                {TOGGLE_SWITCH_VALUE_STATE_LABELS[valueState]}
              </span>
              {TOGGLE_SWITCH_PREVIEW_STATES.map((previewState) => (
                <div
                  key={`${valueState}-${previewState}`}
                  className="toggle-switch-showcase__matrix-cell"
                >
                  <ToggleSwitch
                    {...getValueStateProps(valueState)}
                    previewState={previewState}
                    darkMode={darkMode}
                    aria-label={`${TOGGLE_SWITCH_VALUE_STATE_LABELS[valueState]} ${TOGGLE_SWITCH_PREVIEW_STATE_LABELS[previewState]}`}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Toggle switch"
        sections={propertySections}
      />
    </>
  )
}

export default ToggleSwitchShowcase
